import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';
import {
  getStudentDetails,
  getSearchedStudents,
} from 'layouts/students/service/studentsApi';
import { useDispatch, useSelector } from 'react-redux';
import DialogContent from '@mui/material/DialogContent';
import {
  setSelectedSsaStudent,
  setSelectedStudentsList,
  setSelectedStudentHeadingDetails,
} from '../store/ssaStudentDetailSlice/ssaStudentDetailSlice';
import { getYesNo, getOptionLabel, getDialogHeading } from 'utils/commonUtils';
import { FormInputAutoCompleteSearch } from 'components/Common/Forms/FormAutoCompleteSearch';
import { setSidebarSelectedStudent } from '../store/ssaSidenavBarStudentDetails/ssaSidenavBarStudentDetailsSlice';

const AddStudentToSessionDialog = ({ open, onClose, currentSelectedWeek }) => {
  const sidebarStudents = useSelector(
    (state) => state?.ssaSidenavBarStudentDetails?.sideBarSelectedStudent || [],
  );
  const existingList = useSelector(
    (state) => state?.ssaStudentDetail?.selectedStudentsList || [],
  );

  const dispatch = useDispatch();
  const { setValue } = useForm();

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [studentOptions, setStudentOptions] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setStudentOptions([]);
      setSelectedStudent(null);
      setValue('student_id', '');
    }
  }, [open, setValue]);

  // 🔍 fetch students search
  useEffect(() => {
    if (searchQuery?.length >= 1) fetchStudents();
  }, [searchQuery]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const res = await getSearchedStudents(searchQuery, false, false, 20);
      setStudentOptions(res?.items || []);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  // 👉 when user selects student from autocomplete
  const handleStudentSelect = (val) => {
    setSelectedStudent(val);
    setValue('student_id', val?.studentId);
  };

  const handleStartSession = async () => {
    if (!selectedStudent) return;
    setSessionLoading(true);

    let apiError = false;
    let inCompliance = false;

    try {
      const attendance = await getStudentDetails(selectedStudent.studentId);
      const item = attendance?.item;

      if (item) {
        const weekNote = item?.attendanceData?.notes?.find(
          (n) => n.week === currentSelectedWeek,
        );
        inCompliance = weekNote?.inCompliance ?? false;
      } else {
        apiError = true;
      }
    } catch (err) {
      apiError = true;
    }

    const sidebarObj = {
      apiError,
      inCompliance,
      grade: selectedStudent?.grade,
      id: selectedStudent?.studentId,
      lastName: selectedStudent?.lastName,
      firstName: selectedStudent?.firstName,
    };

    const profileObj = {
      ...sidebarObj,
      semester: selectedStudent?.semester,
      studentId: selectedStudent?.studentId,
    };

    const alreadyExists = sidebarStudents.some(
      (s) => s.id === selectedStudent.studentId,
    );

    if (alreadyExists) {
      setSessionLoading(false);
      onClose();
      return;
    }

    // Place new student at the TOP of the sidebar list
    const updatedList = [sidebarObj, ...sidebarStudents];

    dispatch(setSidebarSelectedStudent(updatedList));

    // Update the selectedStudentsList used in tabs
    const alreadyInSession = existingList?.some(
      (s) => s?.studentId === profileObj?.studentId,
    );

    const updatedSessionList = alreadyInSession
      ? existingList
      : [
          ...existingList,
          {
            grade: profileObj?.grade,
            studentId: profileObj?.studentId,
            lastName: profileObj?.lastName,
            firstName: profileObj?.firstName,
            semester: profileObj?.semester || '',
          },
        ];

    dispatch(setSelectedStudentsList(updatedSessionList));

    dispatch(setSelectedSsaStudent(profileObj?.studentId));
    dispatch(setSelectedStudentHeadingDetails(profileObj));

    onClose();
    setSessionLoading(false);
  };
  return (
    <Dialog open={open} fullWidth>
      {getDialogHeading('Add Student to Session', onClose)}

      <DialogContent>
        <Box>
          <FormInputAutoCompleteSearch
            label="Search students"
            value={selectedStudent}
            options={studentOptions}
            isSearchloading={loading}
            getOptionLabel={getOptionLabel}
            setInputSearchQuery={setSearchQuery}
            setSelectedInput={handleStudentSelect}
            placeholder="Enter student name or ID..."
          />
        </Box>
        {getYesNo(onClose, handleStartSession, sessionLoading)}
      </DialogContent>
    </Dialog>
  );
};

export default AddStudentToSessionDialog;
